import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { getToken } from './localStorageHandler';
import { config as appConfig } from '../../config';
import ScreenAppPrivateClient from '../sdk/client/private';

const baseURL = appConfig.authBaseUrl;
const baseURLV2 = appConfig.authBaseUrlV2;

const token = getToken();

export interface BaseData<T> {
  success: boolean;
  data: T;
  message?: string;
}

export type BaseResponse<T> = AxiosResponse<BaseData<T>>;

const attachChunkedJsonResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response) => {
      if (typeof response.data === 'string') {
        try {
          const trimmed = response.data.trim();
          if (trimmed) {
            response.data = JSON.parse(trimmed);
          }
        } catch {
          // If parsing fails, leave data as-is.
        }
      }
      return response;
    },
    (error) => Promise.reject(error),
  );

  return instance;
};

const api = axios.create({
  baseURL,
  headers: {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  },
});

export const apiV2 = attachChunkedJsonResponseInterceptor(
  axios.create({
    baseURL: baseURLV2,
    headers: {
      'Content-Type': 'application/json',
    },
    // Increase timeout for long-running operations (e.g., file finalization)
    timeout: 300000, // 5 minutes
  }),
);

export const apiV2Public = attachChunkedJsonResponseInterceptor(
  axios.create({
    baseURL: baseURLV2,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 300000,
  }),
);

// Add request interceptor to dynamically get token for each request
apiV2.interceptors.request.use((config) => {
  const currentToken = getToken();
  if (currentToken) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${currentToken}`;
  }
  return config;
});

export const apiV2Factory = (bearerToken?: string) => {
  const requestToken = bearerToken || getToken();
  const instance = attachChunkedJsonResponseInterceptor(
    axios.create({
      baseURL: baseURLV2,
      headers: {
        Authorization: `Bearer ${requestToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 300000, // 5 minutes for long-running operations
    }),
  );

  return instance;
};

export const screenAppAPIClient = new ScreenAppPrivateClient({
  baseUrl: baseURLV2.replace('/v2', ''),
  token: token || '',
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error || !error.response) {
      const er = {
        ...error,
        response: {
          ...error.response,
          data: {
            success: false,
            data: null,
            message: 'Something went wrong. Please try again later.',
          },
        },
      };
      return Promise.reject(er);
    }
    if (error.response.status === 401) {
      if (typeof error.response.data !== 'object') {
        const er = {
          ...error,
          response: {
            ...error.response,
            data: {
              success: false,
              data: null,
              message: 'User unauthorized',
            },
          },
        };
        return Promise.reject(er);
      }
    } else if (error.response.status === 404) {
      if (typeof error.response.data !== 'object') {
        const er = {
          ...error,
          response: {
            ...error.response,
            data: {
              success: false,
              data: null,
              message: 'Something went wrong. Please try again later.',
            },
          },
        };

        return Promise.reject(er);
      }
    }

    return Promise.reject(error);
  },
);

export const setHeader = (accessToken: string) => {
  api.interceptors.request.use((config) => {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = accessToken ? `Bearer ${accessToken}` : '';
    return config;
  });
  apiV2.interceptors.request.use((config) => {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = accessToken ? `Bearer ${accessToken}` : '';
    return config;
  });
  screenAppAPIClient.setToken(accessToken);
};

export default api;
